import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import { Tooltip } from '@katalon-studio/katalon-ui/v2';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';

const useStyles = makeStyles(({
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 0,
    borderRadius: 4,
    border: 'solid 1px #000000',
    width: 127,
    height: 35,
    position: 'relative',
  },
  actiomBtnContainer: {
    height: '100%',
  },
  actiomBtn: {
    width: 35,
    height: '100%',
    fontSize: 20,
    fontWeight: 500,
    color: '#171819',
    '&:disabled': {
      backgroundColor: '#E5E5E5',
    },
  },
  actionBtnLeft: {
    borderRight: 'solid 1px #000000',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  actionBtnRight: {
    borderLeft: 'solid 1px #000000',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  quotaTextField: {
    width: 45,
    height: '100%',
    borderRadius: 0,
    background: '#ffffff',
    textAlign: 'center',
  },
  quotaInputTextField: {
    width: 45,
    fontSize: 14,
    fontWeight: 400,
    color: '#171819',
  },
}));

export interface QuotaInputProps {
  defaultQuantity: number,
  minQuantity?: number,
  productId: string,
  disabled: boolean,
  changeValue: (value: number) => void,
  step?: number,
  tooltipTextId?: string,
}

export const QuotaInput = (
  {
    defaultQuantity,
    minQuantity,
    productId,
    disabled,
    changeValue,
    step,
    tooltipTextId,
  } : QuotaInputProps,
) => {
  const classes = useStyles();
  const [value, setValue] = useState(defaultQuantity);
  const [dirty, setDirty] = useState(false);
  const stepValue = step ?? 1;
  const decreaseDisabled = disabled || value <= (minQuantity ?? 0);
  useEffect(() => {
    setValue(defaultQuantity);
  }, [defaultQuantity]);

  useEffect(() => {
    if (dirty) {
      changeValue(value);
      setDirty(false);
    }
  }, [value]);

  const calculateValue = (value: number): number => {
    if (value <= 0) {
      return 0;
    }
    return Math.ceil(value / stepValue) * stepValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === '' || regex.test(e.target.value)) {
      const inputValue = Number(e.target.value === '' ? 0 : e.target.value);
      if (minQuantity && inputValue < minQuantity) {
        return;
      }
      setValue(inputValue);
      setDirty(true);
      delayDebounceSearchFn(calculateValue(inputValue));
    }
  };

  const handleSubtract = () => {
    changeValueInput(calculateValue(value - stepValue));
  };

  const handleAddition = () => {
    changeValueInput(calculateValue(value + stepValue));
  };

  const changeValueInput = (value: number) => {
    setDirty(true);
    setValue(value);
  };

  const delayDebounceSearchFn = useCallback(debounce(changeValueInput, 1000), []);

  const renderDecreseButton = () => (
    <span className={classes.actiomBtnContainer}>
      <Button
        className={clsx(classes.actiomBtn, classes.actionBtnLeft)}
        id={`${productId}-quota-decrease`}
        style={{ maxWidth: '40px', minWidth: '40px' }}
        onClick={handleSubtract}
        disabled={decreaseDisabled}
        sx={{
          '&:disabled': {
            backgroundColor: '#E5E5E5',
          },
        }}
      >
        -
      </Button>
    </span>
  );

  return (
    <div id={`${productId}-quota`}>
      <div className={classes.inputContainer}>
        {minQuantity && value === minQuantity && tooltipTextId
          ? (
            <Tooltip
              title={(
                <FormattedMessage id={tooltipTextId} />
              )}
              placement="bottom-start"
            >
              {renderDecreseButton()}
            </Tooltip>
          )
          : renderDecreseButton()}
        <TextField
          value={value}
          id={`${productId}-quota-input`}
          variant="outlined"
          sx={{
            '& fieldset': { border: 'none' },
            '&.MuiTextField-root': {
              height: '100%',
            },
          }}
          disabled={disabled}
          InputProps={{
            classes: {
              root: classes.quotaTextField,
              input: classes.quotaInputTextField,
            },
            inputProps: {
              inputMode: 'numeric',
              style: { textAlign: 'center' },
            },
          }}
          onChange={handleChange}
        />
        <Button
          className={clsx(classes.actiomBtn, classes.actionBtnRight)}
          color="primary"
          id={`${productId}-quota-increase`}
          style={{ maxWidth: '40px', minWidth: '40px' }}
          onClick={handleAddition}
          disabled={disabled}
        >
          +
        </Button>
      </div>
    </div>
  );
};
